import { get, put } from "./api";
import { isTourStepInteraction } from "../features/tours/tourEngine";
import type {
  MyToursResponse,
  RecordTourProgressRequest,
  Tour,
  TourKind,
  TourOutcome,
  TourProgress,
  TourStep,
} from "../types/tours";

const TOUR_KINDS: readonly TourKind[] = ["Page", "Data", "FlowStep"];
const TOUR_OUTCOMES: readonly TourOutcome[] = ["Finished", "Skipped"];
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.length > 0;

const nullableString = (value: unknown): string | null =>
  typeof value === "string" && value.length > 0 ? value : null;

const normalizeStep = (raw: unknown): TourStep | null => {
  if (!isRecord(raw) || !isNonEmptyString(raw.key)) return null;
  if (typeof raw.title !== "string" || typeof raw.body !== "string") return null;
  // Absent on an API older than interactive steps, and an unrecognised value is one this build has
  // no behaviour for: both are left off, and `stepInteraction` reads that as "None".
  // `isTourStepInteraction` is the one list of values this build has behaviour for, shared with
  // `stepInteraction` so the guard and the default can never disagree about what is known.
  const interaction = isTourStepInteraction(raw.interaction) ? raw.interaction : undefined;
  return {
    key: raw.key,
    anchor: nullableString(raw.anchor),
    title: raw.title,
    body: raw.body,
    requiredPermission: nullableString(raw.requiredPermission),
    ...(interaction ? { interaction } : {}),
  };
};

/** Exported so the admin preview endpoint, which returns one tour, can reuse the same guard. */
export const normalizeTour = (raw: unknown): Tour | null => {
  if (!isRecord(raw) || !isNonEmptyString(raw.key) || !isNonEmptyString(raw.feature)) {
    return null;
  }
  if (!TOUR_KINDS.includes(raw.kind as TourKind)) return null;
  const steps = Array.isArray(raw.steps)
    ? raw.steps.map(normalizeStep).filter((step): step is TourStep => step !== null)
    : [];
  return {
    key: raw.key,
    feature: raw.feature,
    kind: raw.kind as TourKind,
    ...(typeof raw.name === "string" ? { name: raw.name } : {}),
    steps,
    // Absent on an API older than re-show: null keeps today's behaviour exactly.
    resetAt: isNonEmptyString(raw.resetAt) ? raw.resetAt : null,
  };
};

const normalizeProgress = (raw: unknown): TourProgress | null => {
  if (!isRecord(raw) || !isNonEmptyString(raw.tourKey)) return null;
  return {
    tourKey: raw.tourKey,
    outcome: TOUR_OUTCOMES.includes(raw.outcome as TourOutcome)
      ? (raw.outcome as TourOutcome)
      : null,
    seenStepKeys: Array.isArray(raw.seenStepKeys)
      ? raw.seenStepKeys.filter(isNonEmptyString)
      : [],
  };
};

/**
 * Guards the `me` response at the boundary. Anything malformed is dropped entry by entry rather
 * than failing the whole payload, so one bad step cannot take tours away from every page.
 */
export const normalizeMyTours = (raw: unknown): MyToursResponse => {
  if (!isRecord(raw)) return { tours: [], progress: [] };
  return {
    tours: Array.isArray(raw.tours)
      ? raw.tours.map(normalizeTour).filter((tour): tour is Tour => tour !== null)
      : [],
    progress: Array.isArray(raw.progress)
      ? raw.progress
          .map(normalizeProgress)
          .filter((entry): entry is TourProgress => entry !== null)
      : [],
  };
};

export const getMyTours = async (): Promise<MyToursResponse> => {
  const raw = await get<unknown>("/api/tours/me?platform=Web");
  return normalizeMyTours(raw);
};

export const recordTourProgress = async (
  tourKey: string,
  body: RecordTourProgressRequest
): Promise<void> => {
  await put<unknown>(`/api/tours/progress/${encodeURIComponent(tourKey)}`, body);
};
